import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';

import TypeAheadOptions from '@gumtree/ui-library/src/form/type-ahead/type-ahead-options';
import Input from '@gumtree/ui-library/src/form/input';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Icon } from '@gumtree/ui-library';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { isQA } from '@gumtree/ui-library/src/utils/suggestions-service-g5s';

import * as searchBarActions from '../search-bar-actions';
import {
    StyledLocationContainer,
    StyledTitle,
    StyledTopSection,
    StyledIcon,
    StyledField,
    StyledDialog,
    ErrorWindow,
} from './modal.style';
import {
    locationFieldAriaLabel,
    locationFieldPlaceholderText,
    searchFieldAriaLabel,
    searchFieldPlaceholderText,
} from '../constants';
import { ShellState, ShellUseDispatch } from '../../../reducers/common';
import { ActiveField } from './types';
import useTypeAheadOptionsParams from './type-ahead-options-hook';
import { trackClickSearchBar } from '../search-bar-actions';
import { transformResponseToSuggestions } from '../search-bar-service';

export const getState = (state: ShellState) => {
    return {
        keywordSuggestions: state.searchBar?.keyword?.suggestions || [],
        locationSuggestions: state.searchBar?.location?.suggestions || [],
        searchQuery: state.searchBar?.keyword?.value || '',
        locationQuery: state.searchBar?.location?.value || '',
        keywordUseRecentHistory: state.searchBar?.keyword?.useRecentHistory,
        locationUseRecentHistory: state.searchBar?.location?.useRecentHistory,
        g5SParams: state.searchBar?.g5SParams,
    };
};

export const SearchModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const [currentActiveField, setCurrentActiveField] = useState<ActiveField>(ActiveField.KEYWORD);
    const [keywordInputRef, setKeywordInputRef] = useState<HTMLInputElement | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [shouldRenderTypeAhead, setShouldRenderTypeAhead] = useState(false);
    
    // Local state for immediate input display (not debounced)
    const [localKeywordValue, setLocalKeywordValue] = useState<string>('');
    const [localLocationValue, setLocalLocationValue] = useState<string>('');
    
    // Refs for debounce timers
    const keywordDebounceRef = useRef<number | undefined>(undefined);
    const locationDebounceRef = useRef<number | undefined>(undefined);
    const DEBOUNCE_MS = 250;

    useEffect(() => {
        if (isOpen) {
            const rafId = requestAnimationFrame(() => {
                setTimeout(() => {
                    setShouldRenderTypeAhead(true);
                }, 0);
            });
            return () => cancelAnimationFrame(rafId);
        } else {
            setShouldRenderTypeAhead(false);
            return undefined;
        }
    }, [isOpen]);

    useEffect(() => {
        if (keywordInputRef && isOpen) {
            const rafId = requestAnimationFrame(() => {
                keywordInputRef.focus();
            });
            return () => cancelAnimationFrame(rafId);
        }
        return undefined;
    }, [keywordInputRef, isOpen]);

    const dispatch = useDispatch() as ShellUseDispatch;
    const { searchQuery, locationQuery, g5SParams } = useSelector(getState, shallowEqual);

    useEffect(() => {
        if (isOpen) {
            setLocalKeywordValue(searchQuery);
            setLocalLocationValue(locationQuery);
        } else {
            setLocalKeywordValue('');
            setLocalLocationValue('');
            if (keywordDebounceRef.current) {
                clearTimeout(keywordDebounceRef.current);
                keywordDebounceRef.current = undefined;
            }
            if (locationDebounceRef.current) {
                clearTimeout(locationDebounceRef.current);
                locationDebounceRef.current = undefined;
            }
        }
    }, [isOpen, searchQuery, locationQuery]);

    const handleKeywordChange = useCallback((value: string) => {
        setLocalKeywordValue(value);
        dispatch(searchBarActions.clearKeywordPopulatedBy());
        
        if (keywordDebounceRef.current) {
            clearTimeout(keywordDebounceRef.current);
        }
        
        keywordDebounceRef.current = window.setTimeout(() => {
            const isServicesLandingScoped = Boolean(
                typeof window !== 'undefined' &&
                    (window as any).__servicesLandingKeywordSuggestions
            );

            if (isServicesLandingScoped) {
                dispatch(searchBarActions.setKeyword(value));

                if (value.length === 0) {
                    dispatch(searchBarActions.loadRecentKeywordHistory() as any);
                    keywordDebounceRef.current = undefined;
                    return;
                }

                if (value.length < 2) {
                    dispatch(searchBarActions.setKeywordSuggestions([]));
                    keywordDebounceRef.current = undefined;
                    return;
                }

                if (!g5SParams?.busId || !g5SParams?.timeStamp || !g5SParams?.sign) {
                    dispatch(searchBarActions.setKeywordAndUpdateSuggestions(value));
                    keywordDebounceRef.current = undefined;
                    return;
                }

                const startFetchTime = performance.now();
                const url = isQA()
                    ? 'https://suggestion.58v5.cn/api/searchServicesSug'
                    : 'https://suggestion.ok.com/api/searchServicesSug';

                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify({
                        busId: g5SParams.busId,
                        timeStamp: g5SParams.timeStamp,
                        sign: g5SParams.sign,
                        completion: value,
                        platform: 'web',
                    }),
                })
                    .then((response) => response.json())
                    .then((json) => {
                        const suggestions = transformResponseToSuggestions(json?.hits || []);
                        const fetchTime = performance.now() - startFetchTime;
                        dispatch(searchBarActions.setKeywordSuggestionsLatency(fetchTime));
                        dispatch(searchBarActions.setKeywordSuggestions(suggestions));
                    })
                    .catch(() => {
                        dispatch(searchBarActions.setKeywordSuggestions([]));
                    })
                    .finally(() => {
                        keywordDebounceRef.current = undefined;
                    });

                return;
            }

            dispatch(searchBarActions.setKeywordAndUpdateSuggestions(value));
            keywordDebounceRef.current = undefined;
        }, DEBOUNCE_MS);
    }, [dispatch, g5SParams]);

    const handleLocationChange = useCallback((value: string) => {
        setLocalLocationValue(value);
        
        if (locationDebounceRef.current) {
            clearTimeout(locationDebounceRef.current);
        }
        
        locationDebounceRef.current = window.setTimeout(() => {
            dispatch(searchBarActions.setLocationAndUpdateSuggestions(value));
            locationDebounceRef.current = undefined;
        }, DEBOUNCE_MS);
    }, [dispatch]);

    useEffect(() => {
        return () => {
            if (keywordDebounceRef.current) {
                clearTimeout(keywordDebounceRef.current);
            }
            if (locationDebounceRef.current) {
                clearTimeout(locationDebounceRef.current);
            }
        };
    }, []);

    const typeAheadOptionsParams = useTypeAheadOptionsParams({
        currentActiveField,
        searchQuery,
        locationQuery,
        isOpen,
    });

    const typeAheadIcon = useMemo(
        () => <Icon type={typeAheadOptionsParams.iconType} size="medium" />,
        [typeAheadOptionsParams.iconType]
    );

    return (
        <StyledDialog isOpen={isOpen} onClose={onClose}>
            <StyledTopSection>
                <StyledTitle>Search</StyledTitle>
                <StyledField>
                    <StyledIcon type="magnifying-glass" />
                    <Input
                        {...qaAttribute('search-modal-keyword-input')}
                        aria-label={searchFieldAriaLabel}
                        placeholder={searchFieldPlaceholderText}
                        autoComplete="off"
                        className="input"
                        id="search-modal-keyword-input"
                        name="search-modal-keyword-input"
                        inputRef={(element: HTMLInputElement): void => {
                            setKeywordInputRef(element);
                        }}
                        onFocus={() => {
                            dispatch(
                                trackClickSearchBar('term', typeAheadOptionsParams.options?.length)
                            );
                            setCurrentActiveField(ActiveField.KEYWORD);
                        }}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            const {
                                target: { value },
                            } = event;
                            handleKeywordChange(value);
                        }}
                        onClear={() => {
                            handleKeywordChange('');
                        }}
                        value={localKeywordValue}
                    />
                </StyledField>
                <StyledLocationContainer>
                    <StyledField>
                        <StyledIcon type="beacon" />
                        <Input
                            {...qaAttribute('search-modal-location-input')}
                            aria-label={locationFieldAriaLabel}
                            placeholder={locationFieldPlaceholderText}
                            autoComplete="off"
                            className="input"
                            id="search-modal-location-input"
                            name="search-modal-location-input"
                            onFocus={() => {
                                dispatch(
                                    trackClickSearchBar(
                                        'location',
                                        typeAheadOptionsParams.options?.length
                                    )
                                );
                                setCurrentActiveField(ActiveField.LOCATION);
                            }}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                const {
                                    target: { value },
                                } = event;
                                handleLocationChange(value);
                            }}
                            onClear={() => {
                                handleLocationChange('');
                            }}
                            value={localLocationValue}
                        />
                    </StyledField>
                </StyledLocationContainer>
                {/* A visually-hidden Button is needed so that Enter key in the input will trigger a submit */}
                <button style={{ display: 'none' }} type="submit" />
            </StyledTopSection>
            {shouldRenderTypeAhead && (
                <TypeAheadOptions
                    icon={typeAheadIcon}
                    {...typeAheadOptionsParams}
                />
            )}
            {errorMessage && (
                <ErrorWindow
                    message={errorMessage}
                    onCloseClick={(e) => {
                        e.preventDefault();
                        setErrorMessage(undefined);
                    }}
                />
            )}
        </StyledDialog>
    );
};
